import { useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { FileArchive } from "lucide-react";
import { Modal } from "@/components/panel/ui";
import { Button, Field, Input, Select } from "@/components/ui-kit";

/** Primeiro e último dia de um mês `YYYY-MM`, no formato que o input date usa. */
function limitesDoMes(mes) {
  const [ano, m] = mes.split("-").map(Number);
  const primeiro = new Date(ano, m - 1, 1);
  const ultimo = new Date(ano, m, 0);
  const iso = (d) => d.toLocaleDateString("en-CA");

  return [iso(primeiro), iso(ultimo)];
}

/** O mês anterior ao de hoje — que é o que se manda ao contador. */
function mesPassado() {
  const hoje = new Date();
  const d = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);

  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/**
 * Pacote mensal de XMLs para a contabilidade.
 *
 * Abre já preenchido com o MÊS PASSADO, não com o atual: quem abre esta tela
 * está fechando a competência anterior, e o mês corrente ainda vai receber
 * notas. Deixar o padrão no mês em curso produziria um pacote incompleto com
 * cara de completo.
 *
 * O download não passa pelo `fileUrl` numa aba nova porque a resposta pode ser
 * um 422 ("nenhuma nota no período") — numa aba, isso vira uma página de erro
 * crua. Aqui o blob é baixado pelo axios, que já leva o token, e o erro volta
 * como aviso na própria tela.
 */
export default function ExportXmlsModal({ open, onClose }) {
  const [mes, setMes] = useState(mesPassado);
  const [de, ate] = limitesDoMes(mes);
  const [tipo, setTipo] = useState("");
  const [baixando, setBaixando] = useState(false);

  const baixar = async () => {
    setBaixando(true);
    try {
      const { data, headers } = await api.get("/admin/invoices/export", {
        params: { de, ate, type: tipo || undefined },
        responseType: "blob",
      });

      const url = URL.createObjectURL(new Blob([data], { type: headers["content-type"] || "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `xmls-fiscais-${mes}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      toast.success("ZIP gerado — confira a pasta de downloads.");
      onClose();
    } catch (e) {
      // Erro de blob: a mensagem do servidor vem dentro do Blob, não em JSON.
      let msg = apiError(e);
      const corpo = e?.response?.data;
      if (corpo instanceof Blob) {
        try { msg = JSON.parse(await corpo.text())?.message || msg; } catch { /* mantém a genérica */ }
      }
      toast.error(msg);
    } finally {
      setBaixando(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Enviar XMLs para o contador" testId="fiscal-export-dialog">
      <p className="text-sm text-slate-600 leading-relaxed">
        Gera um ZIP com o XML de cada nota do período, separado em pastas <b>NF-e</b> e <b>NFC-e</b>,
        mais um <b>indice.csv</b> para conferência de quantidade e total.
      </p>

      <Field label="Mês de competência" className="mt-4"
        hint="Abre no mês passado, que é o que se fecha com a contabilidade.">
        <Input type="month" value={mes} onChange={(e) => setMes(e.target.value)}
          data-testid="fiscal-export-month" mono />
      </Field>

      <Field label="O que incluir" className="mt-3">
        <Select value={tipo} onChange={(e) => setTipo(e.target.value)} data-testid="fiscal-export-type">
          <option value="">Tudo — notas e cupons</option>
          <option value="nfe">Só NF-e (notas)</option>
          <option value="nfce">Só NFC-e (cupons)</option>
        </Select>
      </Field>

      {/* Dizer o recorte em voz alta: nota recusada pela SEFAZ não é documento
          fiscal e ficaria como pergunta na mesa do contador; a cancelada VAI,
          porque consumiu numeração e a falta dela abre um buraco na sequência. */}
      <p className="text-xs text-slate-500 mt-3">
        Entram as notas <b>autorizadas</b> e as <b>canceladas</b> emitidas entre {de.split("-").reverse().join("/")} e
        {" "}{ate.split("-").reverse().join("/")}. Tentativas recusadas pela SEFAZ ficam de fora — não têm valor fiscal.
      </p>

      <div className="flex gap-3 mt-5">
        <Button variant="secondary" block onClick={onClose} data-testid="fiscal-export-cancel">Cancelar</Button>
        <Button icon={FileArchive} block loading={baixando} onClick={baixar} data-testid="fiscal-export-confirm">
          {baixando ? "Montando o ZIP..." : "Baixar ZIP"}
        </Button>
      </div>
    </Modal>
  );
}
